import { ProcessTypeEnum, type CreatingApplicationCommand } from '@acme/api';
import type { FormConfig, FormModel } from '@acme/foundation';
import {
  $countryOptions,
  $cryptoCurrencyOptions,
  $currencyOptions,
  $docTypesOptions,
  dictionariesRequested,
} from '@entities/dictionaries';
import {
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector';
import {
  createapplicationformationformconfig,
  type ApplicationFormationFormCtx,
} from './formConfig';
import { createapplicationwalletaddressesformconfig } from './walletAddressesFormConfig';

export type ApplicationFormationSubmitMode = 'draft' | 'review';

export const formMounted = createEvent<{
  defaultData?: ApplicationFormationFormCtx;
  isEdit: boolean;
}>();

export const addWalletClicked = createEvent();
export const resetWallets = createEvent();

export const $isEdit = createStore(false).on(formMounted, (_, p) => p.isEdit);

export const $ctx = createStore<ApplicationFormationFormCtx>({
  __submitKind: 'draft',
}).on(formMounted, (_, p) => ({
  __submitKind: 'draft',
  ...(p.defaultData || {}),
}));

export const $formConfig = combine(
  $isEdit,
  $docTypesOptions,
  $currencyOptions,
  $countryOptions,
  $cryptoCurrencyOptions,
  (
    isEdit,
    docTypesOptions,
    currencyOptions,
    countryOptions,
    cryptoCurrencyOptions
  ) =>
    createapplicationformationformconfig(isEdit, {
      docTypesOptions,
      currencyOptions,
      countryOptions,
      cryptoCurrencyOptions,
    })
);

export const $walletsCount = createStore(1)
  .on(addWalletClicked, (n) => n + 1)
  .on(resetWallets, () => 1)
  .on(formMounted, (_, p) => {
    const addrs = p.defaultData?.cryptoDetails?.walletAddresses;
    return addrs && addrs.length ? addrs.length : 1;
  });

export const $walletsCtx = createStore<Record<string, unknown>>({})
  .on(formMounted, (_, p) => {
    const addrs = p.defaultData?.cryptoDetails?.walletAddresses ?? [];
    const model: Record<string, unknown> = {};
    addrs.forEach((a: string, idx: number) => {
      model[`wallet-${idx}`] = a ?? '';
    });
    return model;
  })
  .on(resetWallets, () => ({}));

export const $walletsConfig = combine(
  $walletsCount,
  (walletsCount): FormConfig<Record<string, unknown>> =>
    createapplicationwalletaddressesformconfig(true, walletsCount, true)
);

export const buildCommandFx = createEffect(
  async ({
    mode,
    form,
    walletsForm,
    walletsCount,
    showIncomingWallets,
  }: {
    mode: ApplicationFormationSubmitMode;
    form: FormModel<ApplicationFormationFormCtx>;
    walletsForm: FormModel<Record<string, unknown>>;
    walletsCount: number;
    showIncomingWallets: boolean;
  }): Promise<Partial<CreatingApplicationCommand>> => {
    let command: Partial<CreatingApplicationCommand> = {};

    await form.submit((model) => {
      command = model;
    });

    if (showIncomingWallets) {
      await walletsForm.submit((walletsModel) => {
        const walletAddresses: string[] = [];
        for (let i = 0; i < walletsCount; i++) {
          const v = walletsModel[`wallet-${i}`];
          const s = String(v ?? '').trim();
          if (s) walletAddresses.push(s);
        }
        command = {
          ...command,
          cryptoDetails: {
            ...(command.cryptoDetails || {}),
            walletAddresses,
          },
        };
      });
    }

    const applicationType = command.applicationType;
    const omitBankDetails =
      applicationType !== ProcessTypeEnum.Outgoing || !!command.isCryptoPayment;
    const base = {
      ...command,
      isDraft: mode === 'draft',
      sendForReview: mode === 'review',
    };
    if (applicationType === ProcessTypeEnum.Incoming) {
      const { bankDetails: _bd, ...rest } = base;
      return { ...rest, isCryptoPayment: true };
    }
    if (omitBankDetails) {
      const { bankDetails: _bd, ...rest } = base;
      return rest;
    }
    return base;
  }
);

export const CRYPTO_FIELD_IDS = [
  'cryptoDetails.cryptoAmount',
  'cryptoDetails.cryptocurrency',
] as const;

export type {
  ApplicationFormActionHandlers,
  ApplicationFormSuccessResult,
  CreateApplicationFn,
  SubmitError,
} from './types';

sample({
  clock: formMounted,
  target: dictionariesRequested,
});
